import { useId } from 'react'
import { Field, FieldGrid, TextInput } from './fields'
import { SeverityChip, StatusChip, VerificationChip, label } from '../vocab'
import type { Incident } from '../api'

/**
 * Step 7 · Review — everything as the desk will receive it, plus the legal references.
 *
 * ## Why the legal block is here and not on step 5
 *
 * An IEBC complaint number, a DCI case number or a court file number usually does not
 * exist at the moment of reporting — they arrive after somebody files. Putting them on the
 * review step means the reporter passes them once, at the end, rather than being asked for
 * four references they cannot have on step five of seven.
 *
 * ## Nothing is validated here
 *
 * The Submit button is offered unconditionally. `POST /incidents/{id}/submit` is what
 * decides, and on a 422 the wizard jumps to the earliest offending step and marks the
 * field. Duplicating those rules in the client would give two answers that can disagree,
 * and the client's would be the wrong one.
 */
export function StepReview({
  incident, value, onChange, errors, onJump,
}: {
  incident: Incident
  value: {
    iebcComplaintRef: string | null
    dciCaseRef: string | null
    courtCaseRef: string | null
    ncicRef: string | null
  }
  onChange: (patch: Partial<typeof value>) => void
  errors: Record<string, string>
  onJump: (step: number) => void
}) {
  const ids = useId()

  return (
    <div className="space-y-5">
      <div className="erp-card p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-erp-xs text-muted-foreground">{incident.ref}</span>
          <StatusChip code={incident.statusCode} />
          <VerificationChip code={incident.verificationCode} />
          <SeverityChip code={incident.severityCode} />
        </div>
        <h3 className="text-erp-md font-semibold">{incident.title}</h3>
        {incident.description && (
          <p className="text-erp-sm text-muted-foreground whitespace-pre-wrap">{incident.description}</p>
        )}
      </div>

      <Summary step={1} onJump={onJump} title="Classification" rows={[
        ['Category', label(incident.categoryCode)],
        ['Subcategory', incident.subcategory],
        ['When', incident.occurredAt ? new Date(incident.occurredAt).toLocaleString() : 'not stated'],
        ['Precision', label(incident.occurredAtPrecisionCode)],
      ]} />

      <Summary step={2} onJump={onJump} title="Narrative" rows={[
        ['Severity', label(incident.severityCode)],
        ['Priority', label(incident.priorityCode)],
      ]} />

      <Summary step={3} onJump={onJump} title="Location" rows={[
        ['Unit', incident.unitName ?? (incident.unitId ? 'named, name not resolved' : null)],
        ['Coordinate', incident.latitude != null ? `${incident.latitude}, ${incident.longitude}` : null],
        ['How it was obtained', incident.locationSourceCode ? label(incident.locationSourceCode) : null],
        ['Accuracy', incident.locationAccuracyM != null ? `±${incident.locationAccuracyM} m` : null],
        ['Place', incident.locationLabel],
        ['Not known because', incident.locationAbsentReason],
      ]} />

      <Summary step={4} onJump={onJump} title="Impact" skippable rows={[
        ['People affected', incident.victimCount],
        ['Injuries', incident.injuryCount],
        ['Deaths', incident.deathCount],
        ['Arrests', incident.arrestsMade],
        ['Voters affected', incident.estimatedVoterImpact],
      ]} />

      <Summary step={5} onJump={onJump} title="Attribution and provenance" skippable rows={[
        ['Alleged perpetrator', incident.perpetrator],
        ['Source outlet', incident.sourceOutlet],
        ['Source link', incident.sourceUrl],
        ['Media coverage', label(incident.mediaCoverageCode)],
      ]} />

      <Summary step={6} onJump={onJump} title="Evidence and tags" skippable rows={[
        ['Evidence items', incident.evidenceCount],
        ['Live feeds', incident.liveFeedCount],
        ['Tags', incident.tags.length > 0 ? incident.tags.join(', ') : null],
      ]} />

      <div className="erp-card p-4">
        <div className="text-erp-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">
          Legal references
        </div>
        <p className="text-erp-xs text-muted-foreground/80 mb-3">
          Fill these in where a complaint or case already exists. They can be added later —
          most of them do not exist yet at the moment of reporting.
        </p>
        <FieldGrid>
          <Field label="IEBC complaint" htmlFor={`${ids}-iebc`} error={errors.iebcComplaintRef}>
            <TextInput id={`${ids}-iebc`} value={value.iebcComplaintRef ?? ''}
              onChange={(v) => onChange({ iebcComplaintRef: v || null })} />
          </Field>
          <Field label="DCI case" htmlFor={`${ids}-dci`} error={errors.dciCaseRef}>
            <TextInput id={`${ids}-dci`} value={value.dciCaseRef ?? ''}
              onChange={(v) => onChange({ dciCaseRef: v || null })} />
          </Field>
          <Field label="Court case" htmlFor={`${ids}-court`} error={errors.courtCaseRef}>
            <TextInput id={`${ids}-court`} value={value.courtCaseRef ?? ''}
              onChange={(v) => onChange({ courtCaseRef: v || null })} />
          </Field>
          <Field label="NCIC reference" htmlFor={`${ids}-ncic`} error={errors.ncicRef}>
            <TextInput id={`${ids}-ncic`} value={value.ncicRef ?? ''}
              onChange={(v) => onChange({ ncicRef: v || null })} />
          </Field>
        </FieldGrid>
      </div>
    </div>
  )
}

function Summary({
  step, title, rows, onJump, skippable,
}: {
  step: number
  title: string
  rows: [string, string | number | null | undefined][]
  onJump: (step: number) => void
  skippable?: boolean
}) {
  const answered = rows.filter(([, v]) => v !== null && v !== undefined && v !== '')
  return (
    <section className="erp-card">
      <header className="flex items-center justify-between gap-3 px-4 py-2 border-b border-border bg-muted/30">
        <span className="text-erp-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {step}. {title}
          {skippable && answered.length === 0 && (
            <span className="ml-2 normal-case tracking-normal font-normal">— skipped</span>
          )}
        </span>
        <button type="button" onClick={() => onJump(step)}
          className="text-erp-xs text-chocolate hover:underline shrink-0">
          Change
        </button>
      </header>
      <dl className="divide-y divide-border/60">
        {answered.length === 0 ? (
          <div className="px-4 py-2 text-erp-sm text-muted-foreground">Nothing recorded.</div>
        ) : answered.map(([k, v]) => (
          <div key={k} className="grid grid-cols-[minmax(96px,34%)_1fr] gap-3 px-4 py-2">
            <dt className="text-erp-xs text-muted-foreground pt-0.5">{k}</dt>
            <dd className="text-erp-sm min-w-0 break-words">{v}</dd>
          </div>
        ))}
      </dl>
    </section>
  )
}
